import { useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Card,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import type { VaultRiskBreach } from 'src/pages/admin/services/vaultService';

import { RISK_ROWS_PER_PAGE_OPTIONS, VAULTS_NS as NS } from './constants';
import { formatBreachFigure, formatInstant, isOpenBreach, type RiskBadge } from './helpers';
import type { VaultRiskBreachesState } from './useVaultRiskBreaches';

const NUMERIC = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

/**
 * The header badge (XBIT-4764): "Risk: N open", red when a FORCED_DELEVERAGE episode is open and amber for any other,
 * absent at 0, and a grey "Risk: unavailable" when the breach read failed (never "0").
 */
export const RiskBadgeChip = ({ badge }: { badge: RiskBadge }) => {
  const translate = useTranslate();
  if (badge.kind === 'hidden') return null;
  if (badge.kind === 'unavailable') {
    return (
      <Chip
        size="small"
        color="default"
        variant="outlined"
        data-testid="risk-badge"
        data-kind="unavailable"
        label={translate(`${NS}.detail.risk.badge.unavailable`)}
      />
    );
  }
  return (
    <Chip
      size="small"
      color={badge.kind === 'critical' ? 'error' : 'warning'}
      data-testid="risk-badge"
      data-kind={badge.kind}
      label={translate(`${NS}.detail.risk.badge.open`, { count: badge.count })}
    />
  );
};

const Row = ({
  breach,
  showClosed,
  timeZone,
}: {
  breach: VaultRiskBreach;
  showClosed: boolean;
  timeZone?: string;
}) => {
  const translate = useTranslate();
  return (
    <TableRow data-testid="risk-row">
      <TableCell>{translate(`${NS}.detail.risk.limits.${breach.limit}`)}</TableCell>
      <TableCell sx={NUMERIC}>{formatInstant(breach.openedAt, timeZone)}</TableCell>
      {showClosed && (
        <TableCell sx={NUMERIC}>
          {isOpenBreach(breach) ? (
            <Chip size="small" color="warning" label={translate(`${NS}.detail.risk.stillOpen`)} />
          ) : (
            formatInstant(breach.closedAt as string, timeZone)
          )}
        </TableCell>
      )}
      <TableCell align="right" sx={NUMERIC}>
        {formatBreachFigure(breach.limit, breach.valueAtOpen)}
      </TableCell>
      <TableCell align="right" sx={NUMERIC}>
        {formatBreachFigure(breach.limit, breach.limitAtOpen)}
      </TableCell>
      <TableCell sx={{ overflowWrap: 'anywhere' }}>{breach.detail ?? '—'}</TableCell>
    </TableRow>
  );
};

/**
 * The "Risk breaches" section of the vault detail (XBIT-4764): the open episodes, newest first, with a "Show all"
 * toggle for every episode (open and closed, C-06 `open: false`) paged like the console's other lists. Read-only: the
 * monitor alerts and nothing here acts on a breach. Times read in the viewer's zone (`timeZone` defaults to the
 * browser's), as everywhere on the vault console.
 */
export const RiskBreachesCard = ({
  risk,
  timeZone,
}: {
  risk: VaultRiskBreachesState;
  timeZone?: string;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.detail.risk.${key}`, options);
  const items = risk.page?.items ?? [];
  return (
    <Card sx={{ p: 3 }} data-testid="risk-card">
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Box>
          <Typography variant="subtitle1">{t('title')}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('hint')}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={risk.showAll}
              onChange={(_, checked) => risk.toggleShowAll(checked)}
              inputProps={{ 'aria-label': t('showAll') }}
            />
          }
          label={t('showAll')}
        />
      </Stack>
      {risk.showAll && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('showAllHint')}
        </Typography>
      )}

      {risk.failed && (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="risk-unavailable">
          {t('unavailable')}
        </Alert>
      )}

      {!risk.failed && risk.page && items.length === 0 && (
        <Typography sx={{ mt: 2, color: 'text.secondary' }} data-testid="risk-empty">
          {t(risk.showAll ? 'emptyAll' : 'emptyOpen')}
        </Typography>
      )}

      {!risk.failed && items.length > 0 && (
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('table.limit')}</TableCell>
              <TableCell>{t('table.openedAt')}</TableCell>
              {risk.showAll && <TableCell>{t('table.closedAt')}</TableCell>}
              <TableCell align="right">{t('table.value')}</TableCell>
              <TableCell align="right">{t('table.limitValue')}</TableCell>
              <TableCell>{t('table.detail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((breach) => (
              <Row key={breach.id} breach={breach} showClosed={risk.showAll} timeZone={timeZone} />
            ))}
          </TableBody>
        </Table>
      )}

      {risk.showAll && risk.page && risk.page.total > 0 && (
        <TablePagination
          component="div"
          count={risk.page.total}
          page={risk.pageIndex}
          onPageChange={(_, next) => risk.setPageIndex(next)}
          rowsPerPage={risk.rowsPerPage}
          onRowsPerPageChange={(e) => risk.setRowsPerPage(parseInt(e.target.value, 10))}
          rowsPerPageOptions={RISK_ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={t('rowsPerPage')}
        />
      )}
    </Card>
  );
};
